import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listHospitals } from '../services/hospitalService';
import type { Hospital } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

const PAGE_SIZE = 10;

const activeFilterOptions = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export default function HospitalListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchHospitals = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listHospitals({
        search: search || undefined,
        active: activeFilter || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setHospitals(result.hospitals);
      setTotal(result.total);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load hospitals';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [search, activeFilter, page]);

  useEffect(() => {
    fetchHospitals();
  }, [fetchHospitals]);

  // Reset to page 1 when search or filter changes
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (value: string) => {
    setActiveFilter(value);
    setPage(1);
  };

  const isAdmin = user?.role === 'ADMIN';

  return (
    <div className="hospital-list-page">
      <div className="page-header">
        <h2>Hospital Management</h2>
        {isAdmin && (
          <Button onClick={() => navigate('/hospitals/create')}>
            Create Hospital
          </Button>
        )}
      </div>

      <div className="filters-row">
        <Input
          placeholder="Search by name or code..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          aria-label="Search hospitals"
        />
        <Select
          label="Status"
          options={activeFilterOptions}
          value={activeFilter}
          onChange={(e) => handleFilterChange(e.target.value)}
          aria-label="Filter by active status"
        />
      </div>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <LoadingSpinner message="Loading hospitals..." />
      ) : (
        <>
          <table className="data-table" role="table">
            <thead>
              <tr>
                <th scope="col">Hospital Code</th>
                <th scope="col">Hospital Name</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {hospitals.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-row">
                    No hospitals found.
                  </td>
                </tr>
              ) : (
                hospitals.map((hospital) => (
                  <tr key={hospital.hospitalUuid}>
                    <td>{hospital.hospitalCode}</td>
                    <td>{hospital.hospitalName}</td>
                    <td>
                      <span
                        className={`status-badge ${hospital.isActive ? 'status-active' : 'status-inactive'}`}
                      >
                        {hospital.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/hospitals/${hospital.hospitalUuid}/edit`)}
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/hospitals/${hospital.hospitalUuid}/history`)}
                      >
                        History
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="pagination" role="navigation" aria-label="Pagination">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="pagination-info">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
